import { useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  PaywallRenewSubscriptionDoc,
  PaywallRenewSubscriptionMutationVariables,
  PaywallRenewSubscriptionMutation,
  PaywallPreviewSubscriptionRenewalDoc,
  PaywallPreviewSubscriptionRenewalQueryVariables,
  PaywallPreviewSubscriptionRenewalQuery,
  PaywallSubscriptionPurchaseMutation,
} from '@minecraft.graphql-operations';
import { gqlBillingInfo } from '@minecraft.graphql-types';
import { captureExceptionEvent } from '@blocs.observability';
import { useUserContext } from '@blocs.auth';
import { useTranslation } from '@blocs.i18n';
import { publishPurchaseFlowEvent } from '@blocs.marketing-events';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { PaywallLocation } from './enums/PaywallLocation';
import { UsePayment } from './types';
import { PlanType } from './usePlans';
import { PaywallEvents } from './enums/events';

export type Renewal = {
  name: PaywallPreviewSubscriptionRenewalQuery['previewSubscriptionRenewal']['subscription']['name'];
  cardType: PaywallPreviewSubscriptionRenewalQuery['previewSubscriptionRenewal']['cardType'];
  currency: PaywallPreviewSubscriptionRenewalQuery['previewSubscriptionRenewal']['currency'];
  email: PaywallPreviewSubscriptionRenewalQuery['previewSubscriptionRenewal']['email'];
  invoiceId: PaywallPreviewSubscriptionRenewalQuery['previewSubscriptionRenewal']['invoiceId'];
  lastFour: PaywallPreviewSubscriptionRenewalQuery['previewSubscriptionRenewal']['lastFour'];
  subscription: PaywallPreviewSubscriptionRenewalQuery['previewSubscriptionRenewal']['subscription'];
  total: PaywallPreviewSubscriptionRenewalQuery['previewSubscriptionRenewal']['total'];
};

type usePaywallRenewalProps = {
  selectedPlan: PlanType;
  selectedPaymentOption?: gqlBillingInfo;
  paywallLocation: PaywallLocation;
  setCompletedPurchase?: UsePayment['setCompletedPurchase'];
  setCompletePurchaseError?: UsePayment['setCompletePurchaseError'];
  isAnnualRenewalDue?: boolean;
  isAnnual?: boolean;
  onNext: () => void;
};

export type usePaywallRenewal = {
  loading?: boolean;
  error?: Error;
  currentRenewalPurchase?: Renewal;
  couponLoading?: boolean;
  completeRenewalPurchase: () => Promise<void>;
  renewalCurrencyCode: string;
  enableAnnualRenewal: boolean;
};

export const usePaywallRenewal = ({
  selectedPlan,
  selectedPaymentOption,
  paywallLocation,
  setCompletedPurchase = () => null,
  setCompletePurchaseError = () => null,
  isAnnualRenewalDue,
  isAnnual,
  onNext,
}: usePaywallRenewalProps): usePaywallRenewal => {
  const { showMessage } = useGlobalNotification();
  const { refreshTokens, subscription } = useUserContext();
  const { t } = useTranslation();

  const [completeRenewalMutation, { error: completeRenewalErrorFromNetwork, data: completeRenewalData }] = useMutation<
    PaywallRenewSubscriptionMutation,
    PaywallRenewSubscriptionMutationVariables
  >(PaywallRenewSubscriptionDoc, {
    refetchQueries: ['artistSubscriptions'],
    awaitRefetchQueries: true,
  });

  const paymentOptionId = selectedPaymentOption?.id ?? subscription?.billingInfoId;
  const { data: subscriptionRenewalData } = useQuery<
    PaywallPreviewSubscriptionRenewalQuery,
    PaywallPreviewSubscriptionRenewalQueryVariables
  >(PaywallPreviewSubscriptionRenewalDoc, {
    variables: {
      input: {
        billingInfoId: String(paymentOptionId),
        subscriptionId: Number(subscription?.id),
      },
    },
    skip: !(paymentOptionId && ((isAnnualRenewalDue && isAnnual) || completeRenewalData)),
  });

  useEffect(() => {
    const preview = subscriptionRenewalData?.previewSubscriptionRenewal;
    if (preview && selectedPlan && !completeRenewalData) {
      publishPurchaseFlowEvent(PaywallEvents.beginCheckout, {
        location: paywallLocation,
        purchase: {
          subscriptions: [
            {
              name: `${selectedPlan?.title} ${selectedPlan?.term?.code} Renewal`,
              code: `${selectedPlan?.billingCode}_renewal`,
              price: preview.subscription?.price,
            },
          ],
          total: preview?.total,
          currency: preview?.currency,
        },
      });
    }
  }, [subscriptionRenewalData, paywallLocation, selectedPlan]);

  useEffect(() => {
    if (completeRenewalErrorFromNetwork) {
      setCompletePurchaseError('Unknown error');
    }
  }, [completeRenewalErrorFromNetwork]);

  const completeRenewalPurchase = async () => {
    try {
      setCompletedPurchase({ loading: true, data: null });

      const response = await completeRenewalMutation({
        variables: {
          input: { billingInfoId: String(paymentOptionId), subscriptionId: Number(subscription?.id) },
        },
      });

      const purchase = response.data.renewSubscription;
      if (purchase && selectedPlan) {
        publishPurchaseFlowEvent(PaywallEvents.purchase, {
          location: paywallLocation,
          purchase: {
            subscriptions: [
              {
                name: `${selectedPlan?.title} ${selectedPlan?.term?.code} Renewal`,
                code: `${selectedPlan?.billingCode}_renewal`,
                price: purchase.total,
              },
            ],
            total: purchase.total,
            currency: purchase.currency,
          },
          transactionId: purchase.invoiceId,
          currentPlan: subscription?.plan?.billingCode,
        });
        await refreshTokens();
        setCompletedPurchase({
          loading: false,
          data: response.data
            ?.renewSubscription as unknown as PaywallSubscriptionPurchaseMutation['purchaseSubscription'],
        });
        showMessage({
          color: 'success',
          message: t('common:paywall.payment.success'),
        });
        onNext();
      } else {
        setCompletedPurchase({
          loading: false,
          data: null,
        });
        setCompletePurchaseError(t('common:paywall.errors.TRANSACTION_ERROR'));
        throw new Error(t('common:paywall.errors.TRANSACTION_ERROR'));
      }
    } catch (err) {
      console.error(err, { selectedPaymentOption, selectedPlanBillingCode: selectedPlan?.billingCode });
      captureExceptionEvent(err);
      throw err;
    }
  };

  const currentRenewalPurchase = useMemo(() => {
    if (subscriptionRenewalData?.previewSubscriptionRenewal) {
      const { subscription, cardType, lastFour, invoiceId, email, currency, total } =
        subscriptionRenewalData.previewSubscriptionRenewal;
      const sub = {
        ...(subscription || {}),
        plan: { name: subscription.name, billingCode: subscription?.code },
      };
      return {
        currency,
        email,
        invoiceId,
        cardType,
        lastFour,
        subscription: sub,
        total,
        name: subscription.name,
      } as Renewal;
    }

    return undefined;
  }, [subscriptionRenewalData?.previewSubscriptionRenewal]);

  const enableAnnualRenewal = isAnnualRenewalDue && isAnnual && selectedPlan?.canRenew !== false;

  return {
    currentRenewalPurchase,
    completeRenewalPurchase,
    renewalCurrencyCode: subscriptionRenewalData?.previewSubscriptionRenewal.currency.code,
    enableAnnualRenewal,
  };
};
